import { useState, useEffect } from 'react';
import {
  DialogContent,
  DialogContentText,
  Typography,
  Button,
  Box,
  Card,
  Chip,
  TextField,
} from '@mui/material';
import { useAccount } from '../../../contexts/Account';
import {
  answerSurveyQuestion,
  getActiveSurvey,
  getSurveyResponses,
} from '../../../services/survey_services';
import { addRelationshipIntroduction } from '../../../services/advisor_services';
import ACSnackbar from '../../../components/ACSnackbar';
import LoadingLogoSkeleton from '../../../components/SkeletonLoading/loadingLogoSkeleton';
import { addActivityFromAnswerSurvey } from '../../../services/utils/surveys';
import eventBus, { eventBusValues } from '../../../eventBus';

const useStyles = () => ({
  dialogContent: {
    padding: { xxs: '20px', xs: '10px' },
    overflow: 'visible',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  dialogTextCompanyName: {
    textAlign: 'center',
  },
  surveyButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: { xxs: '11px', xs: '14px' },
    width: { xxs: '100%', xs: '200px' },
    height: { xxs: '28px', xs: '36px' },
    borderRadius: '10px',
  },
  chip: {
    position: 'absolute',
    fontSize: '10px',
    height: '18px',
    top: -10,
    left: { xxs: 4, xs: -10 },
    zIndex: 10,
    backgroundColor: '#4FB5F9',
    color: '#F5F7FA',
    fontWeight: 600,
    padding: '10px 15px',
  },
  card: {
    backgroundColor: '#F5F7FA',
    padding: { xxs: '18px 30px', xs: '20px 40px' },
    position: 'relative',
    overflow: 'visible',
    margin: '20px 0px',
    borderRadius: '16px',
    width: { xxs: '75%', xs: '100%' },
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '130%',
    letterSpacing: 0,
  },
});

function getIntro({ accountDetails, companyBoardData }) {
  try {
    const board = accountDetails?.boards.memberBoards.find(
      (company) => company.id === companyBoardData.id
    );
    if (board && board.introduction) {
      return board.introduction;
    }
    if (accountDetails.aobfPrimaryExpertise) {
      return `Hi everyone, I'm excited to join the ${board.companyName} advisory board. My background is in ${accountDetails.aobfPrimaryExpertise} and I'm looking forward to sharing ideas.`;
    }

    return `Hi everyone, I'm excited to join the ${board.companyName} advisory board.`;
  } catch (error) {
    console.error('Error getting introduction:', error);
    return '';
  }
}

export default function Step2({
  setActiveStep,
  companyBoardData,
  setBlockClosePopup,
}) {
  const { accountDetails, type } = useAccount();
  const [surveyAnswer, setSurveyAnswer] = useState(null);
  const [oldAnswerBody, setOldAnswerBody] = useState(null);
  const [intro, setIntro] = useState(
    getIntro({ accountDetails, companyBoardData })
  );
  const companyIsP2P = companyBoardData.boardType == "P2P";
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);
  const [singleQuestion, setSingleQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const classes = useStyles();

  async function handleSurveyResponse(params) {
    if (!surveyAnswer) {
      return;
    }
    if (singleQuestion && oldAnswerBody === surveyAnswer) {
      // console.log('Same survey answer, skipping update');
      return;
    }
    await answerSurveyQuestion({
      BODY: surveyAnswer,
      SURVEY_ID: singleQuestion.surveyId,
      SURVEY_QUESTION_ID: singleQuestion.id,
      USER_ACCOUNT_TYPE: type,
      USER_ID: accountDetails.id,
    });

    if (oldAnswerBody) {
      return; // If oldAnswerBody is set, we assume this is an update to an existing answer
    }
    addActivityFromAnswerSurvey({
      accountDetails,
      surveyAnswer,
      questionBody: singleQuestion.body,
    });
  }

  async function handleIntroUpdate() {
    if (!intro) {
      return;
    }
    try {
      const board = accountDetails?.boards.memberBoards.find(
        (company) => company.id === companyBoardData.id
      );
      if (board && board.introduction && board.introduction === intro) {
        // console.log('Same introduction value, skipping update');
        return;
      }
    } catch (error) {
      console.error('Error validating same value:', error);
    }
    await addRelationshipIntroduction({
      introduction: intro,
      advisorId: accountDetails.id,
      companyId: companyBoardData.id,
    });

    eventBus.dispatch(
      eventBusValues.introAdded,
      companyIsP2P ? 
        `<b>${accountDetails.displayName}</b> joined the boardroom. "${intro}"` :
        `<b>${accountDetails.displayName}</b> joined the advisory board. "${intro}"`
    );
  }

  async function handleNext() {
    try {
      setLoading(true);
      setBlockClosePopup(true);
      await Promise.all([handleSurveyResponse(), handleIntroUpdate()]);
      setActiveStep((prev) => prev + 1);
    } catch (error) {
      console.error('Error handling next step:', error);
      setIsSnackbarVisible(true);
    } finally {
      setLoading(false);
      setBlockClosePopup(false);
    }
  }

  useEffect(async () => {
    let isMounted = true;

    if (isMounted && companyBoardData) {
      const { data } = await getActiveSurvey({
        COMPANY_ID: companyBoardData.id,
      });
      const questions = data.getActiveSurvey;
      if (questions && questions.length > 0) {
        setSingleQuestion(questions[0]);
        const response = await getSurveyResponses({
          USER_ID: accountDetails.id,
          SURVEY_ID: questions[0].surveyId,
        });

        if (response && response.data && response.data.getSurveyResponses) {
          const surveyResponses = response.data.getSurveyResponses;
          if (surveyResponses && surveyResponses.length > 0) {
            setSurveyAnswer(surveyResponses[0].body);
            setOldAnswerBody(surveyResponses[0].body);
          }
        }
      } else {
        setSingleQuestion(null);
      }
    }

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <DialogContent sx={classes.dialogContent}>
      <DialogContentText sx={classes.dialogTextCompanyName}>
        <Typography
          variant="caption"
          sx={{
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '-0.43px',
            textTransform: 'none',
            fontWeight: 700,
            color: '#232B35',
            verticalAlign: 'middle',
            marginTop: '8px',
          }}
        >
          {`${accountDetails.givenName}, let's get you involved...`}
        </Typography>
      </DialogContentText>
      {singleQuestion && (
        <Card sx={classes.card} elevation={0}>
          <Chip label="Survey" size="small" sx={classes.chip} />
          <Typography sx={classes.cardTitle}>{singleQuestion.body}</Typography>
          <Box
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: {xxs: '1fr', xs: '1fr 1fr'},
              gap: 1,
              mt: '16px',
            }}
          >
            {singleQuestion.responseOptions.map((option, index) => (
              <Button
                key={index}
                variant={surveyAnswer === option ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => {
                  setSurveyAnswer(option);
                }}
                sx={classes.surveyButton}
                disabled={loading}
              >
                {option}
              </Button>
            ))}
          </Box>
        </Card>
      )}

      <Card sx={classes.card} elevation={0}>
        <Chip
          label="Introduction"
          size="small"
          sx={{
            ...classes.chip,
            backgroundColor: '#3578F4',
          }}
        />
        <Typography
          sx={{
            ...classes.cardTitle,
            textAlign: 'center',
          }}
        >
          {companyIsP2P
            ? 'Introduce yourself to your fellow advisors:'
            : 'Introduce yourself to your new advisory board:'}
        </Typography>
        <TextField
          margin="dense"
          label=""
          type="text"
          fullWidth
          multiline
          rows={3}
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          variant="outlined"
          disabled={loading}
          sx={{ backgroundColor: '#FFFFFF' }}
        />
      </Card>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: '0px',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleNext}
          disabled={loading}
          className="getStartedButton"
          sx={{
            paddingTop: '15px',
            paddingBottom: '15px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '15px',
            width: '180px',
            height: '44px',
            borderRadius: '30px',
          }}
        >
          {loading ? <LoadingLogoSkeleton /> : 'Next'}
        </Button>
      </Box>
      <ACSnackbar
        onClose={() => setIsSnackbarVisible(false)}
        open={isSnackbarVisible}
        severity="error"
        text="Something went wrong"
      />
    </DialogContent>
  );
}
